import { motion } from 'framer-motion';
import { ChevronRight, FileCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CARD_CLASS, EYEBROW_CLASS, OMS_COLORS, sectionVariants } from '../omsDashTheme';
import type { OmsApprovalsSummary } from '../types';
import StatCount from './StatCount';

export default function ApprovalsStrip({ approvals }: { approvals: OmsApprovalsSummary }) {
  const navigate = useNavigate();
  return (
    <motion.section
      variants={sectionVariants}
      initial="hidden"
      animate="visible"
      className={`${CARD_CLASS} border-l-4 !border-l-[#C9A961] p-4`}
      aria-label="Pending approvals"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#C9A961]/15">
            <FileCheck className="h-5 w-5" style={{ color: OMS_COLORS.champagneDeep }} />
          </div>
          <div>
            <p className={EYEBROW_CLASS}>Pending approvals</p>
            <StatCount
              label=""
              value={approvals.pendingCount}
              noun={approvals.pendingCount === 1 ? 'project' : 'projects'}
              qualifier="awaiting decision"
              size="lg"
              animated
              ink={OMS_COLORS.champagneDeep}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate('/approvals/projects')}
          className="flex items-center gap-1 rounded-xl bg-[#1F5A47] px-3 py-2 text-xs font-bold text-[#F2EADB] transition hover:bg-[#0B3B2E]"
        >
          Review all <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      {approvals.items.length > 0 && (
        <ul className="mt-3 flex gap-2 overflow-x-auto pb-1">
          {approvals.items.map((item) => (
            <li
              key={item.id}
              className="min-w-[220px] shrink-0 rounded-xl border border-[rgba(11,59,46,0.18)] bg-white/60 px-3 py-2"
            >
              <p className="truncate text-xs font-bold" style={{ color: OMS_COLORS.ink }}>{item.projectName}</p>
              <p className="truncate text-[11px]" style={{ color: OMS_COLORS.slate }}>
                {item.requesterName} · {item.relativeTime}
              </p>
              <span
                className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                  item.status === 'resubmitted' ? 'bg-[#339274]/15 text-[#1F5A47]' : 'bg-[#C9A961]/20 text-[#8A5A08]'
                }`}
              >
                {item.status === 'resubmitted' ? 'Re-submitted' : 'Pending'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </motion.section>
  );
}
